/**
 * Tickets — an open support board. Any signed-in user can file a ticket and see
 * every ticket; only admins triage (status / resolution). Each ticket has a
 * submitter↔admin comment thread (admin internal notes hidden from others).
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ErrorCard } from '../components/ErrorCard'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Textarea,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Add16Regular } from '@fluentui/react-icons'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { useToast } from '../components/ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { NewTicketDialog } from '../components/NewTicketDialog'
import { PersonPicker } from '../components/PersonPicker'
import { EditableField } from '../components/EditableField'
import {
  useAddTicketComment,
  useTicket,
  useTicketAssignees,
  useTicketUsers,
  useTickets,
  useUpdateTicket,
} from '../hooks/useTickets'
import { useTicketImagePaste } from '../hooks/useTicketImagePaste'
import { TicketMarkdown } from '../components/TicketMarkdown'
import type {
  Ticket,
  TicketPriority,
  TicketStatus,
  TicketType,
  TicketUpdateInput,
} from '../types/ticket'
import { CURRENT_VERSION, RELEASES } from '../data/whatsNew'

// Where the mirror issues live. Hardcoded because the backend already owns the
// repo identity (FEEDBACK_REPO_OWNER / FEEDBACK_REPO_NAME) and shipping it to
// the client just to build a link would mean a new API field to keep in step.
// If the repo ever moves, this is the one place to change.
const GITHUB_REPO = 'bayer-int/DACTalentFlow'

const TYPES: { value: TicketType; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature request' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
]
const STATUSES: { value: TicketStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]
const STATUS_COLOR: Record<TicketStatus, 'warning' | 'brand' | 'success' | 'informative'> = {
  open: 'warning',
  in_progress: 'brand',
  resolved: 'success',
  closed: 'informative',
}
const PRIORITIES: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]
const PRIORITY_COLOR: Record<TicketPriority, 'informative' | 'subtle' | 'danger'> = {
  low: 'informative',
  medium: 'subtle',
  high: 'danger',
}

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: tokens.spacingHorizontalM },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center', flexWrap: 'wrap' },
  tableWrap: { background: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusMedium, border: `1px solid ${tokens.colorNeutralStroke2}`, overflowX: 'auto' },
  row: { cursor: 'pointer' },
  empty: { padding: '48px 16px', textAlign: 'center', color: tokens.colorNeutralForeground3 },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '420px' },
  detailMeta: { color: tokens.colorNeutralForeground3, fontSize: '12px' },
  thread: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, marginTop: tokens.spacingVerticalM, maxHeight: '260px', overflowY: 'auto' },
  comment: { padding: '8px 10px', borderRadius: tokens.borderRadiusMedium, background: tokens.colorNeutralBackground2 },
  internal: { background: '#FFF4CE', border: '1px solid #F2D98E' },
  context: { marginTop: tokens.spacingVerticalS, padding: '8px 10px', borderRadius: tokens.borderRadiusMedium, background: tokens.colorNeutralBackground2, display: 'flex', flexDirection: 'column', gap: '2px' },
  contextLabel: { color: tokens.colorNeutralForeground3 },
  triage: { marginTop: tokens.spacingVerticalM, padding: tokens.spacingVerticalM, borderRadius: tokens.borderRadiusMedium, border: `1px solid ${tokens.colorNeutralStroke2}`, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
})

function typeLabel(t: TicketType) {
  return TYPES.find((x) => x.value === t)?.label ?? t
}
function statusLabel(s: TicketStatus) {
  return STATUSES.find((x) => x.value === s)?.label ?? s
}
function priorityLabel(p: TicketPriority) {
  return PRIORITIES.find((x) => x.value === p)?.label ?? p
}
/** The ship date for a release version, from the What's New list (or null). */
function releaseDate(version: string | null | undefined): string | null {
  if (!version) return null
  return RELEASES.find((r) => r.version === version)?.date ?? null
}

/** A status/priority badge that, for admins, opens a menu to change it inline. */
function MenuBadge({
  editable,
  badge,
  options,
  onPick,
  testid,
}: {
  editable: boolean
  badge: ReactNode
  options: { value: string; label: string }[]
  onPick: (value: string) => void
  testid: string
}) {
  if (!editable) return <>{badge}</>
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          data-testid={testid}
          title="Click to change"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          {badge}
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {options.map((o) => (
            <MenuItem key={o.value} onClick={() => onPick(o.value)}>
              {o.label}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  )
}

export function Tickets() {
  const styles = useStyles()
  const navigate = useNavigate()
  const [selectedId, setSelectedId] = useState<number | null>(null)

  /**
   * `/tickets/{id}` opens that ticket.
   *
   * Ticket notifications and their emails carry `link=/tickets/{id}`
   * (services/ticket_notifications.py) and there was no route to match it, so
   * every one of them fell through to the catch-all and dropped the reader on
   * their home page — after the bell had already marked it read. Adding the
   * route makes the links that were ALREADY SENT work; changing the link
   * format would only have fixed future ones.
   *
   * Deliberately an entry point rather than derived view state (contrast
   * #1248, where the timeline filters must track the URL continuously): the
   * dialog is opened and closed far more often than it is linked to, and
   * routing every open would make this page unusable outside a route. An
   * effect rather than an initialiser because the bell can be clicked while
   * already on this page, which changes the param without remounting.
   */
  const { id: routeTicketId } = useParams<{ id?: string }>()
  useEffect(() => {
    if (routeTicketId && /^[0-9]+$/.test(routeTicketId)) setSelectedId(Number(routeTicketId))
  }, [routeTicketId])

  const openTicket = (id: number) => setSelectedId(id)
  const closeTicket = () => {
    setSelectedId(null)
    // Drop the deep link so closing does not re-open on the next route read.
    if (routeTicketId) navigate('/tickets', { replace: true })
  }
  const user = useEffectiveUser()
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  const toast = useToast()

  const [statusFilter, setStatusFilter] = useState('')
  const [mine, setMine] = useState(false)
  const [fixedFilter, setFixedFilter] = useState('')
  const { data, isLoading, error, refetch } = useTickets({ status: statusFilter, mine })
  const allTickets = data?.items ?? []
  // Distinct fix versions present, for the "fixed in version" milestone filter.
  const fixVersions = [
    ...new Set(allTickets.map((t) => t.fixed_in_version).filter((v): v is string => !!v)),
  ].sort()
  // Sort so the board scans fast: active statuses first, then high priority,
  // then newest. (Terminal resolved/closed sink to the bottom.)
  const STATUS_RANK: Record<string, number> = { open: 0, in_progress: 1, resolved: 2, closed: 3 }
  const PRIORITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 }
  const tickets = [
    ...(fixedFilter ? allTickets.filter((t) => t.fixed_in_version === fixedFilter) : allTickets),
  ].sort((a, b) => {
    const s = (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9)
    if (s) return s
    const p = (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9)
    if (p) return p
    return (b.created_at || '').localeCompare(a.created_at || '')
  })

  const [newOpen, setNewOpen] = useState(false)

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <Title2 block>Tickets</Title2>
        </div>
        <Button appearance="primary" icon={<Add16Regular />} onClick={() => setNewOpen(true)} data-testid="new-ticket-btn">
          New ticket
        </Button>
      </div>

      <div className={styles.filters}>
        <Dropdown
          placeholder="All statuses"
          value={statusFilter ? statusLabel(statusFilter as TicketStatus) : 'All statuses'}
          selectedOptions={[statusFilter]}
          onOptionSelect={(_, d) => setStatusFilter((d.optionValue as string) ?? '')}
          data-testid="ticket-status-filter"
        >
          <Option value="">All statuses</Option>
          {STATUSES.map((s) => (
            <Option key={s.value} value={s.value}>
              {s.label}
            </Option>
          ))}
        </Dropdown>
        <Switch checked={mine} onChange={(_, d) => setMine(d.checked)} label="Only mine" />
        {fixVersions.length > 0 && (
          <Dropdown
            placeholder="Fixed in any version"
            value={fixedFilter ? `Fixed in v${fixedFilter}` : 'Fixed in any version'}
            selectedOptions={[fixedFilter]}
            onOptionSelect={(_, d) => setFixedFilter((d.optionValue as string) ?? '')}
            data-testid="ticket-fixed-filter"
          >
            <Option value="">Fixed in any version</Option>
            {fixVersions.map((v) => (
              <Option key={v} value={v} text={`v${v}`}>
                {`v${v}`}
              </Option>
            ))}
          </Dropdown>
        )}
      </div>

      {isLoading ? (
        <div className={styles.empty}>
          <Spinner size="small" />
        </div>
      ) : error ? (
        <div data-testid="tickets-error">
          <ErrorCard
            title="Couldn't load tickets"
            message={apiErrorMessage(error, 'Please try again.')}
            retry={() => {
              void refetch()
            }}
          />
        </div>
      ) : tickets.length === 0 ? (
        <div className={styles.empty}>No tickets yet. File one with “New ticket”.</div>
      ) : (
        <div className={styles.tableWrap}>
          <Table aria-label="Tickets" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '38%' }}>Subject</TableHeaderCell>
                <TableHeaderCell style={{ width: '11%' }}>Type</TableHeaderCell>
                <TableHeaderCell style={{ width: '13%' }}>Status</TableHeaderCell>
                <TableHeaderCell style={{ width: '10%' }}>Priority</TableHeaderCell>
                <TableHeaderCell style={{ width: '16%' }}>Submitter</TableHeaderCell>
                <TableHeaderCell style={{ width: '12%' }}>Filed</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((t: Ticket) => (
                <TableRow
                  key={t.id}
                  className={styles.row}
                  onClick={() => openTicket(t.id)}
                  data-testid={`ticket-row-${t.id}`}
                >
                  <TableCell>
                    <Button appearance="transparent" size="small" onClick={(e) => {
                      e.stopPropagation()
                      openTicket(t.id)
                    }}>{t.subject}</Button>
                  </TableCell>
                  <TableCell>
                    <Caption1>{typeLabel(t.type)}</Caption1>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={STATUS_COLOR[t.status]}>
                      {statusLabel(t.status)}
                    </Badge>
                    {t.fixed_in_version && (
                      <Caption1
                        block
                        style={{ color: tokens.colorPaletteGreenForeground1, fontSize: '11px' }}
                      >
                        Fixed v{t.fixed_in_version}
                      </Caption1>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      appearance={t.priority === 'high' ? 'filled' : 'tint'}
                      color={PRIORITY_COLOR[t.priority]}
                      data-testid={`ticket-priority-${t.id}`}
                    >
                      {priorityLabel(t.priority)}
                    </Badge>
                  </TableCell>
                  <TableCell>{t.submitter_name ?? '—'}</TableCell>
                  <TableCell>
                    <Caption1>{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</Caption1>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NewTicketDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(id) => {
          toast.showToast({ title: 'Ticket filed — we’ll follow up.' })
          setNewOpen(false)
          openTicket(id)
        }}
      />

      {selectedId != null && (
        <TicketDetailDialog id={selectedId} isAdmin={isAdmin} onClose={closeTicket} />
      )}
    </div>
  )
}

function TicketDetailDialog({ id, isAdmin, onClose }: { id: number; isAdmin: boolean; onClose: () => void }) {
  const styles = useStyles()
  const toast = useToast()
  const { data: t, isLoading, isError, refetch } = useTicket(id)
  const addComment = useAddTicketComment(id)
  const update = useUpdateTicket(id)
  const { data: assignees = [] } = useTicketAssignees(isAdmin)
  const { data: ticketUsers = [] } = useTicketUsers(isAdmin)

  const [comment, setComment] = useState('')
  const [internal, setInternal] = useState(false)

  // The ticket exists here, so images bind on upload — no second step.
  const commentImages = useTicketImagePaste({
    ticketId: id,
    onInsert: useCallback(
      (md: string) => setComment((prev) => (prev.trim() ? prev + '\n\n' + md : md)),
      [],
    ),
    onError: useCallback(
      (message: string) =>
        toast.showToast({ title: "Couldn't attach the image", body: message, intent: 'error' }),
      [toast],
    ),
  })

  function postComment() {
    if (!comment.trim()) return
    addComment.mutate(
      { body: comment.trim(), is_internal: isAdmin ? internal : false },
      {
        onSuccess: () => {
          setComment('')
          setInternal(false)
        },
        onError: (err) =>
          toast.showToast({
            title: "Couldn't post the comment",
            body: apiErrorMessage(err),
            intent: 'error',
          }),
      },
    )
  }

  // Immediate single-field PATCH — every triage control saves as you go.
  // Saving as you go means a rejected PATCH has no Save button to stay stuck
  // on: without the error toast the control simply snapped back.
  function patch(fields: TicketUpdateInput, msg = 'Ticket updated') {
    if (!t) return
    update.mutate(fields, {
      onSuccess: () => toast.showToast({ title: msg }),
      onError: (err) =>
        toast.showToast({
          title: "Couldn't save the change",
          body: apiErrorMessage(err),
          intent: 'error',
        }),
    })
  }

  // Inline title edit — admins only; saves immediately.
  function saveTitle(_path: string, value: string) {
    const v = value.trim()
    if (!t || !v || v === t.subject) return
    patch({ subject: v }, 'Title updated')
  }

  // Status change (click-to-change badge / Resolve-Reopen). Prefills the fix
  // version when resolving/closing without one, matching the old triage form.
  function changeStatus(s: TicketStatus) {
    if (!t || s === t.status) return
    const fields: TicketUpdateInput = { status: s }
    if ((s === 'resolved' || s === 'closed') && !t.fixed_in_version) {
      fields.fixed_in_version = CURRENT_VERSION
    }
    patch(fields, `Status → ${statusLabel(s)}`)
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {t ? (
              <>
                #{t.id} ·{' '}
                <EditableField
                  value={t.subject}
                  path="subject"
                  onSave={saveTitle}
                  disabled={!isAdmin}
                  placeholder="Ticket title"
                />
              </>
            ) : (
              'Ticket'
            )}
          </DialogTitle>
          <DialogContent>
            {isLoading ? (
              <Spinner size="small" />
            ) : isError || !t ? (
              // `isLoading || !t` sent a failed fetch back to the spinner
              // forever: isLoading goes false, `t` stays undefined, and the
              // ticket the user just clicked becomes unreachable from here.
              <div role="alert" data-testid="ticket-detail-error" style={{ minWidth: 440 }}>
                <MessageBar intent="error">
                  <MessageBarBody>Couldn&apos;t load this ticket.</MessageBarBody>
                </MessageBar>
                <div style={{ marginTop: 12 }}>
                  <Button size="small" appearance="secondary" onClick={() => void refetch()}>
                    Try again
                  </Button>
                </div>
              </div>
            ) : (
              <div data-testid="ticket-detail" style={{ minWidth: 440 }}>
                <div
                  className={styles.detailMeta}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
                >
                  <span>{typeLabel(t.type)}</span>
                  <span>·</span>
                  <MenuBadge
                    editable={isAdmin}
                    testid="ticket-status"
                    options={STATUSES}
                    onPick={(v) => changeStatus(v as TicketStatus)}
                    badge={
                      <Badge appearance="tint" color={STATUS_COLOR[t.status]}>
                        {statusLabel(t.status)}
                      </Badge>
                    }
                  />
                  <span>·</span>
                  <MenuBadge
                    editable={isAdmin}
                    testid="ticket-priority"
                    options={PRIORITIES}
                    onPick={(v) => patch({ priority: v as TicketPriority }, `Priority → ${priorityLabel(v as TicketPriority)}`)}
                    badge={
                      <Badge
                        appearance={t.priority === 'high' ? 'filled' : 'tint'}
                        color={PRIORITY_COLOR[t.priority]}
                      >
                        {priorityLabel(t.priority)}
                      </Badge>
                    }
                  />
                  <span>· by {t.submitter_name ?? '—'}</span>
                  {t.assigned_to_name && <span>· assigned to {t.assigned_to_name}</span>}
                  {t.fixed_in_version && (
                    <span>
                      · fixed in v{t.fixed_in_version}
                      {releaseDate(t.fixed_in_version) ? ` (${releaseDate(t.fixed_in_version)})` : ''}
                    </span>
                  )}
                  {/* The mirror issue. Status flows GitHub → ticket, so this is
                      where the state on screen actually comes from once a
                      maintainer starts working it — worth being one click away
                      rather than a number nobody can act on. */}
                  {t.github_issue_number && (
                    <span>
                      ·{' '}
                      <a
                        href={`https://github.com/${GITHUB_REPO}/issues/${t.github_issue_number}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="ticket-github-link"
                      >
                        GitHub #{t.github_issue_number}
                      </a>
                    </span>
                  )}
                  {isAdmin &&
                    (t.status === 'resolved' || t.status === 'closed' ? (
                      <Button
                        size="small"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => changeStatus('open')}
                        data-testid="ticket-reopen"
                      >
                        Reopen
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        appearance="primary"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => changeStatus('resolved')}
                        data-testid="ticket-resolve"
                      >
                        Resolve
                      </Button>
                    ))}
                </div>
                <Body1 block style={{ marginTop: 8 }}>
                  <TicketMarkdown>{t.body}</TicketMarkdown>
                </Body1>
                {t.resolution_note && (
                  <Caption1 block style={{ marginTop: 8, color: tokens.colorNeutralForeground2 }}>
                    Resolution: {t.resolution_note}
                  </Caption1>
                )}

                <div className={styles.context} data-testid="ticket-context">
                  {t.submitter_role && (
                    <Caption1 className={styles.contextLabel}>
                      Submitter: {t.submitter_role}
                      {t.submitter_focus_area_code ? ` · ${t.submitter_focus_area_code}` : ''}
                    </Caption1>
                  )}
                  {t.details?.page_url && (
                    <Caption1 className={styles.contextLabel}>Page: {t.details.page_url}</Caption1>
                  )}
                  {t.details?.app_version && (
                    <Caption1 className={styles.contextLabel}>
                      Reported on v{t.details.app_version}
                      {t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString()}` : ''}
                    </Caption1>
                  )}
                  {t.details?.user_agent && (
                    <Caption1 className={styles.contextLabel}>Browser: {t.details.user_agent}</Caption1>
                  )}
                  {t.details?.expected && (
                    <Caption1 className={styles.contextLabel}>Expected: {t.details.expected}</Caption1>
                  )}
                  {t.details?.steps && (
                    <Caption1 className={styles.contextLabel}>Steps: {t.details.steps}</Caption1>
                  )}
                  {t.details?.problem && (
                    <Caption1 className={styles.contextLabel}>Problem solved: {t.details.problem}</Caption1>
                  )}
                  {t.related_demand_id && (
                    <Caption1 className={styles.contextLabel}>Linked demand: #{t.related_demand_id}</Caption1>
                  )}
                </div>

                <div className={styles.thread}>
                  {t.comments.map((c) => (
                    <div key={c.id} className={`${styles.comment}${c.is_internal ? ` ${styles.internal}` : ''}`}>
                      <Caption1 block style={{ color: tokens.colorNeutralForeground3 }}>
                        {c.author_name ?? '—'}
                        {c.is_internal ? ' · internal' : ''}
                      </Caption1>
                      <TicketMarkdown>{c.body}</TicketMarkdown>
                    </div>
                  ))}
                </div>

                <Field style={{ marginTop: 12 }}>
                  <Textarea
                    value={comment}
                    onChange={(_, d) => setComment(d.value)}
                    rows={2}
                    placeholder="Add a comment… (Markdown; paste a screenshot to attach it)"
                    data-testid="ticket-comment-input"
                    onPaste={commentImages.onPaste}
                    onDrop={commentImages.onDrop}
                    onDragOver={commentImages.onDragOver}
                  />
                </Field>
                {commentImages.uploading && (
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    Uploading image…
                  </Caption1>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                  <Button size="small" appearance="primary" disabled={!comment.trim() || addComment.isPending} onClick={postComment} data-testid="ticket-comment-submit">
                    Comment
                  </Button>
                  {isAdmin && (
                    <Switch checked={internal} onChange={(_, d) => setInternal(d.checked)} label="Internal note" />
                  )}
                </div>

                {isAdmin && (
                  <div className={styles.triage} data-testid="ticket-triage">
                    <Caption1 style={{ fontWeight: 600 }}>Triage (admin) · edits save as you go</Caption1>
                    <Field label="Assignee">
                      <PersonPicker
                        currentName={t.assigned_to_name}
                        userId={t.assigned_to}
                        roleLabel="assignee"
                        users={assignees}
                        onSelect={(u) =>
                          patch({ assigned_to: u ? u.id : 0 }, u ? `Assigned to ${u.name}` : 'Unassigned')
                        }
                      />
                    </Field>
                    <Field label="Submitter (reporter)">
                      <PersonPicker
                        currentName={t.submitter_name}
                        userId={t.submitter_user_id}
                        roleLabel="reporter"
                        users={ticketUsers}
                        allowFreehand
                        onSelect={(u) => u && patch({ submitter_user_id: u.id }, `Reporter → ${u.name}`)}
                        onFreehandSelect={(name) => patch({ submitter_freehand: name }, `Reporter → ${name}`)}
                      />
                    </Field>
                    <Field label="Fixed in version">
                      <EditableField
                        value={t.fixed_in_version}
                        path="fixed_in_version"
                        placeholder={`e.g. ${CURRENT_VERSION}`}
                        onSave={(_p, v) => patch({ fixed_in_version: v.trim() || null }, 'Fix version saved')}
                      />
                    </Field>
                    <Field label="Resolution note">
                      <EditableField
                        value={t.resolution_note}
                        path="resolution_note"
                        multiline
                        placeholder="Add a resolution note…"
                        onSave={(_p, v) => patch({ resolution_note: v.trim() || null }, 'Resolution note saved')}
                      />
                    </Field>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="subtle" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
